import type {
  SelectOption,
  VendorRequest,
  VendorResponse,
} from 'src/api/ProactApiClient';
import type {PillProps} from 'src/design/atoms/Pill';
import type {ISelectOption} from 'src/types/Form';
import type {IFormFieldV2} from 'src/types/FormV2';
import {emptyToUndefined} from 'src/types/FormV2Mapping';

const ManagerField: IFormFieldV2 = {
  name: 'managerId',
  label: 'Manager',
  type: 'person',
  description:
    'Select the vendor manager. This person will be automatically assigned as the supervisor for all contractors under this vendor.',
};

export const vendorInsuranceFormMeta: IFormFieldV2[] = [
  {
    name: 'certificateOfInsuranceAttachmentUrl',
    label: 'Certificate of Insurance',
    type: 'file',
    required: false,
    validation: {
      maxLength: 10000,
    },
  },
  {
    name: 'certificateOfInsuranceExpiresAt',
    label: 'Certificate of Insurance Expires On',
    type: 'date',
    required: false,
  },
];

export const VendorBaseFormMeta = (
  usergroups: SelectOption[]
): IFormFieldV2[] => [
  {
    name: 'name',
    label: 'Name',
    type: 'text',
    required: true,
  },
  {
    name: 'description',
    label: 'Description',
    type: 'text',
    required: false,
    validation: {
      maxLength: 1000,
    },
  },
  ManagerField,
  {
    name: 'autoUserGroupIds',
    label: 'Auto-Assign User Groups',
    type: 'select-multiple',
    options: usergroups as ISelectOption[],
    required: false,
    description:
      'Associate a user group to this vendor. When users are assigned to this vendor, they will automatically be added to the user group. This can be used to automate training and task assignments. Note: removing the user group from this vendor does not remove the users from the user group.',
  },
  {
    label: 'Company Info',
    name: 'heading1',
    type: 'heading',
  },
  {
    name: 'email',
    label: 'Email',
    type: 'text',
    required: false,
  },
  {
    name: 'phoneNumber',
    label: 'Phone Number',
    type: 'phone',
    required: false,
  },
  {
    name: 'address1',
    label: 'Address Line 1',
    type: 'text',
    required: false,
  },
  {
    name: 'address2',
    label: 'Address Line 2',
    type: 'text',
    required: false,
  },
  {
    name: 'city',
    label: 'City',
    type: 'text',
    required: false,
  },
  {
    name: 'state',
    label: 'State',
    type: 'text',
    required: false,
  },
  {
    name: 'country',
    label: 'Country',
    type: 'text',
    required: false,
  },
  {
    name: 'zipCode',
    label: 'Zip Code',
    type: 'text',
    required: false,
  },
];

export const VendorFormMeta = (usergroups: SelectOption[]): IFormFieldV2[] => [
  ...VendorBaseFormMeta(usergroups),

  {
    label: 'Certificate of Insurance',
    name: 'heading2',
    type: 'heading',
  },
  ...vendorInsuranceFormMeta,
];

export const MapVendorResponseToVendorRequest = (
  data: VendorResponse
): VendorRequest => {
  return {
    id: data.id,
    name: data.name,
    description: data.description,
    managerId: emptyToUndefined(data.managerId) as number | undefined,
    email: data.email,
    phoneNumber: data.phoneNumber,
    address1: data.address1,
    address2: data.address2,
    city: data.city,
    state: data.state,
    country: data.country,
    zipCode: data.zipCode,
    certificateOfInsuranceAttachmentUrl:
      data.certificateOfInsuranceAttachmentUrl,
    certificateOfInsuranceExpiresAt: data.certificateOfInsuranceExpiresAt,
  };
};

export const GetVendorPillConfig = (active?: boolean): PillProps[] => {
  const pills: PillProps[] = [];
  if (active === true) {
    pills.push({text: 'Active', color: 'green'});
  } else {
    pills.push({text: 'Inactive', color: 'yellow'});
  }

  return pills;
};

export const VendorDocumentTitle = 'Vendors';
